import type { BlogPostData } from '@workspace/api-backend'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { FormField } from '@workspace/ui/components/form-field'
import { TextInput } from '@workspace/ui/components/text-input'
import { Textarea } from '@workspace/ui/components/textarea'
import { ImageIcon, Loader2, Trash2 } from 'lucide-react'
import { useRef, useState } from 'react'
import { TiptapEditor } from '../../../components/tiptap-editor'
import { useImageUpload } from '../../../hooks/use-image-upload'
import { TagInput } from './tag-input'

interface BlogFormProps {
  formId?: string
  initialData?: BlogPostData
  onSubmit: (data: BlogFormData) => void
  isSubmitting: boolean
}

export interface BlogFormData {
  title: string
  slug?: string
  content: string
  excerpt: string
  metaDescription: string
  coverImageUrl: string
  tags: string[]
  targetQueries: string[]
  status: 'DRAFT' | 'PUBLISHED'
}

export function BlogForm({ formId, initialData, onSubmit, isSubmitting }: BlogFormProps) {
  const [title, setTitle] = useState(initialData?.title ?? '')
  const [slug, setSlug] = useState(initialData?.slug ?? '')
  const [content, setContent] = useState(initialData?.content ?? '')
  const [excerpt, setExcerpt] = useState(initialData?.excerpt ?? '')
  const [metaDescription, setMetaDescription] = useState(initialData?.metaDescription ?? '')
  const [coverImageUrl, setCoverImageUrl] = useState(initialData?.coverImageUrl ?? '')
  const [tags, setTags] = useState<string[]>(initialData?.tags ?? [])
  const [targetQueries, setTargetQueries] = useState<string[]>(initialData?.targetQueries ?? [])

  const coverInputRef = useRef<HTMLInputElement>(null)
  const { upload: uploadCover, isUploading: isUploadingCover } = useImageUpload({
    onSuccess: setCoverImageUrl,
  })

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) await uploadCover(file)
    if (coverInputRef.current) coverInputRef.current.value = ''
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit({
      title,
      slug: initialData ? slug : undefined,
      content,
      excerpt: excerpt || '',
      metaDescription: metaDescription || '',
      coverImageUrl: coverImageUrl || '',
      tags,
      targetQueries,
      status: initialData?.status ?? 'DRAFT',
    })
  }

  return (
    <form id={formId} onSubmit={handleSubmit} className="space-y-6">
      {/* Title */}
      <FormField label="Title" required>
        <TextInput
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Blog post title"
          required
        />
      </FormField>

      {/* Slug (only on edit) */}
      {initialData && (
        <FormField label="Slug">
          <TextInput
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="url-friendly-slug"
          />
        </FormField>
      )}

      {/* Cover Image */}
      <FormField label="Cover Image">
        {coverImageUrl ? (
          <div className="relative">
            <img
              src={coverImageUrl}
              alt="Cover"
              className="h-48 w-full rounded-lg border border-border object-cover"
            />
            <button
              type="button"
              onClick={() => setCoverImageUrl('')}
              className="absolute top-2 right-2 rounded-full bg-black/60 p-1.5 text-white hover:bg-black/80"
            >
              <Trash2 className="size-4" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => coverInputRef.current?.click()}
            disabled={isUploadingCover}
            className="flex h-32 w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border text-sm text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground"
          >
            {isUploadingCover ? (
              <Loader2 className="size-5 animate-spin" />
            ) : (
              <>
                <ImageIcon className="size-5" />
                Click to upload cover image
              </>
            )}
          </button>
        )}
        <input
          ref={coverInputRef}
          type="file"
          accept="image/*"
          onChange={handleCoverUpload}
          className="hidden"
        />
      </FormField>

      {/* Content */}
      <FormField label="Content" required>
        <TiptapEditor
          content={content}
          onChange={setContent}
          placeholder="Write your blog post..."
        />
      </FormField>

      {/* Excerpt */}
      <FormField label="Excerpt" hint="Max 500 chars">
        <Textarea
          value={excerpt}
          onChange={(e) => setExcerpt(e.target.value)}
          placeholder="Brief summary of the post..."
          maxLength={500}
          rows={3}
        />
      </FormField>

      {/* Meta Description */}
      <FormField label="Meta Description" hint={`${metaDescription.length}/160`}>
        <Textarea
          value={metaDescription}
          onChange={(e) => setMetaDescription(e.target.value)}
          placeholder="SEO-optimized description..."
          maxLength={160}
          rows={2}
        />
      </FormField>

      {/* Tags */}
      <FormField label="Tags">
        <TagInput value={tags} onChange={setTags} placeholder="Add tags..." />
      </FormField>

      {/* Target Queries */}
      <FormField label="SEO Target Queries" hint="SEO keywords">
        <TagInput
          value={targetQueries}
          onChange={setTargetQueries}
          placeholder="Add SEO keywords..."
        />
      </FormField>

      {/* Submit — only shown when no external formId */}
      {!formId && (
        <div className="flex justify-end border-t border-border pt-6">
          <CtaButton type="submit" loading={isSubmitting} disabled={!title || !content}>
            {initialData ? 'Save changes' : 'Create post'}
          </CtaButton>
        </div>
      )}
    </form>
  )
}
